import type { A2uiContext, A2uiValue } from '@/types/a2ui';

function getPointer(obj: unknown, path: string): unknown {
  if (!path || path === '/') return obj;
  return path
    .split('/')
    .slice(1)
    .reduce<unknown>((value, key) => {
      if (value == null) return undefined;
      return (value as Record<string, unknown>)[key];
    }, obj);
}

/**
 * Absolute paths ("/x") resolve against the surface's root data model; bare
 * paths ("x") resolve against the current template scope (the item bound by
 * an enclosing List/Column template), per the A2UI spec's collection-scope rules.
 */
export function resolvePath(ctx: A2uiContext, path: string): unknown {
  return path.startsWith('/') ? getPointer(ctx.root, path) : getPointer(ctx.scope, `/${path}`);
}

function setPointer(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('/').slice(1);
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (typeof cur[key] !== 'object' || cur[key] === null) {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]] = value;
}

export function setPath(ctx: A2uiContext, path: string, value: unknown): void {
  if (path.startsWith('/')) {
    setPointer(ctx.root, path, value);
  } else {
    setPointer(ctx.scope, `/${path}`, value);
  }
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function formatDate(value: unknown, format: string): string {
  const date = new Date(value as string | number);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return format.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d/g, (token) => {
    switch (token) {
      case 'yyyy':
        return String(date.getFullYear());
      case 'yy':
        return String(date.getFullYear()).slice(-2);
      case 'MMMM':
        return MONTHS[date.getMonth()];
      case 'MMM':
        return MONTHS[date.getMonth()].slice(0, 3);
      case 'MM':
        return pad(date.getMonth() + 1);
      case 'M':
        return String(date.getMonth() + 1);
      case 'dd':
        return pad(date.getDate());
      case 'd':
        return String(date.getDate());
      default:
        return token;
    }
  });
}

export function formatCurrency(value: unknown, currency: string): string {
  const amount = Number(value);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

/** Resolves a declarative A2UI value expression (literal, path, or formatting call) against a context. */
export function resolveValue(val: A2uiValue | undefined, ctx: A2uiContext): string {
  if (val == null) return '';
  if (typeof val !== 'object') return String(val);
  if ('path' in val) return String(resolvePath(ctx, val.path) ?? '');
  if ('call' in val) {
    const args = val.args ?? {};
    if (val.call === 'formatString') return formatString(resolveValue(args.value, ctx), ctx);
    if (val.call === 'formatDate') return formatDate(resolveValue(args.value, ctx), resolveValue(args.format, ctx));
    if (val.call === 'formatCurrency') {
      return formatCurrency(resolveValue(args.value, ctx), resolveValue(args.currency, ctx));
    }
  }
  return '';
}

// Splits "name:value, name2:value2" on top-level commas only (ignoring commas
// nested inside ${...} expressions or inside 'quoted' literals).
function splitArgs(argsStr: string): string[] {
  let depth = 0;
  let inQuote = false;
  let cur = '';
  const parts: string[] = [];
  for (let k = 0; k < argsStr.length; k++) {
    const ch = argsStr[k];
    if (ch === "'") inQuote = !inQuote;
    if (!inQuote) {
      if (ch === '$' && argsStr[k + 1] === '{') depth++;
      else if (ch === '}' && depth > 0) depth--;
    }
    if (ch === ',' && depth === 0 && !inQuote) {
      parts.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) parts.push(cur);
  return parts;
}

function evalExpr(expr: string, ctx: A2uiContext): unknown {
  const trimmed = expr.trim();
  const call = trimmed.match(/^(\w+)\((.*)\)$/s);
  if (call) {
    const args: Record<string, unknown> = {};
    for (const part of splitArgs(call[2])) {
      const i = part.indexOf(':');
      const name = part.slice(0, i).trim();
      const rawValue = part.slice(i + 1).trim();
      const inner = rawValue.match(/^\$\{(.*)\}$/s);
      args[name] = inner ? evalExpr(inner[1], ctx) : rawValue.replace(/^'|'$/g, '');
    }
    if (call[1] === 'formatDate') return formatDate(args.value, String(args.format));
    return '';
  }
  return resolvePath(ctx, trimmed) ?? '';
}

// Interpolates ${...} spans in a template, tracking brace depth so nested
// ${...} (e.g. inside a formatDate(value:${/x}, ...) call) resolves correctly.
export function formatString(template: string, ctx: A2uiContext): string {
  let out = '';
  let i = 0;
  while (i < template.length) {
    if (template[i] === '$' && template[i + 1] === '{') {
      let depth = 1;
      let j = i + 2;
      while (j < template.length && depth > 0) {
        if (template[j] === '$' && template[j + 1] === '{') {
          depth++;
          j += 2;
          continue;
        }
        if (template[j] === '}') {
          depth--;
          j++;
          continue;
        }
        j++;
      }
      out += String(evalExpr(template.slice(i + 2, j - 1), ctx));
      i = j;
    } else {
      out += template[i];
      i++;
    }
  }
  return out;
}
